import { difficultyFor } from "./difficulty.ts";
import { qualifies, insertEntry } from "./storage.ts";
import type {
  GameEvent,
  GameState,
  LeaderboardEntry,
  ScoreResult,
} from "./types.ts";

export function initialState(): GameState {
  return {
    phase: "IDLE",
    round: 0,
    score: 0,
    lives: 3,
    currentQuestion: null,
    promptShownAt: null,
    lastFeedback: null,
    lastReactionMs: null,
    birdAnim: "idle",
    initials: "AAA",
    initialsCursor: 0,
    leaderboard: [],
  };
}

export function scoreFor(reactionMs: number, windowMs: number): ScoreResult {
  if (reactionMs < 200) return { feedback: "PERFECT", points: 100 };
  if (reactionMs < 500) return { feedback: "GOOD", points: 50 };
  if (reactionMs <= windowMs) return { feedback: "OK", points: 20 };
  return { feedback: "TOO LATE", points: 0 };
}

export function transition(state: GameState, event: GameEvent): GameState {
  switch (state.phase) {
    case "IDLE":
      if (event.type === "KEY_Y") {
        return {
          ...state,
          phase: "ASKING",
          round: 1,
          score: 0,
          lives: 3,
          currentQuestion: event.question ?? null,
          promptShownAt: null,
          lastFeedback: null,
          lastReactionMs: null,
          birdAnim: "peck",
        };
      }
      return state;

    case "ASKING":
      if (event.type === "TIMER_FIRED") {
        return { ...state, phase: "PROMPT", promptShownAt: event.now ?? 0 };
      }
      if (event.type === "KEY_Y") {
        return {
          ...state,
          phase: "PENALTY",
          lives: state.lives - 1,
          lastFeedback: "TOO EARLY",
          birdAnim: "peck",
        };
      }
      return state;

    case "PROMPT":
      if (event.type === "KEY_Y") {
        if (state.promptShownAt === null || event.now === undefined) {
          return state;
        }
        const reactionMs = event.now - state.promptShownAt;
        const { windowMs } = difficultyFor(state.round);
        const { feedback, points } = scoreFor(reactionMs, windowMs);
        return {
          ...state,
          phase: "FEEDBACK",
          lastFeedback: feedback,
          lastReactionMs: reactionMs,
          score: state.score + points,
          birdAnim: "peck",
        };
      }
      if (event.type === "TIMER_FIRED") {
        return {
          ...state,
          phase: "PENALTY",
          lives: state.lives - 1,
          lastFeedback: "TOO LATE",
        };
      }
      return state;

    case "FEEDBACK":
      if (event.type === "TIMER_FIRED") {
        return {
          ...state,
          phase: "ASKING",
          round: state.round + 1,
          currentQuestion: event.question ?? null,
          promptShownAt: null,
          birdAnim: "idle",
        };
      }
      return state;

    case "PENALTY":
      if (event.type === "TIMER_FIRED") {
        if (state.lives <= 0) {
          return { ...state, phase: "GAME_OVER", birdAnim: "slump" };
        }
        return {
          ...state,
          phase: "ASKING",
          round: state.round + 1,
          currentQuestion: event.question ?? null,
          promptShownAt: null,
          birdAnim: "idle",
        };
      }
      return state;

    case "GAME_OVER":
      if (event.type === "TIMER_FIRED") {
        if (qualifies(state.score, state.leaderboard)) {
          return {
            ...state,
            phase: "LEADERBOARD_ENTRY",
            initials: "AAA",
            initialsCursor: 0,
          };
        }
        return { ...initialState(), leaderboard: state.leaderboard };
      }
      return state;

    case "LEADERBOARD_ENTRY":
      if (event.type === "KEY_ARROW_UP" || event.type === "KEY_ARROW_DOWN") {
        const delta = event.type === "KEY_ARROW_UP" ? 1 : -1;
        const chars = state.initials.split("");
        const code = chars[state.initialsCursor]!.charCodeAt(0) - 65;
        const next = (((code + delta) % 26) + 26) % 26;
        chars[state.initialsCursor] = String.fromCharCode(65 + next);
        return { ...state, initials: chars.join("") };
      }
      if (event.type === "KEY_Y") {
        if (state.initialsCursor < 2) {
          return { ...state, initialsCursor: state.initialsCursor + 1 };
        }
        const entry: LeaderboardEntry = {
          initials: state.initials,
          score: state.score,
          round: state.round,
          date: event.date ?? "",
        };
        const newBoard = insertEntry(state.leaderboard, entry);
        return { ...initialState(), leaderboard: newBoard };
      }
      return state;

    case "BOOT":
      return state;

    default:
      return state;
  }
}
